import { GMI, STATE } from "../../constants";
import replacement from "../../services/gma/replacement";
import { isNonEmptyObject } from "../../utils/js/object";
import { types } from "../../utils/js/types";
import { batchActions } from "../../utils/redux";
import request from "../request";

/**
 * @typedef {object} PaymentInitiatePayload
 * @prop {string} success_url Redirect URL in case payment is completed successfully
 * @prop {string} failure_url Redirect URL in case payment fails to complete
 * @prop {string} style_sheet_url Direct URL for stylesheet to be applied on the payment iframe
 */

const { REPLACEMENT } = STATE.UI;

/**
 * @typedef { "card_holder_name" | "card_type" | "credit_card_expired" | "credit_card_near_expiration" | "description" | "expiration_date" | "last_four" | "payment_reference_id" | "preferred" | "sequence" | "masked_number" | "payment_type"} FieldNames
 */

/** @type {Record<FieldNames, string[]>} */
const FIELD_PATHS = {
  card_holder_name: REPLACEMENT.PAYMENT.CONFIRM.CARD_HOLDER_NAME,
  card_type: REPLACEMENT.PAYMENT.CONFIRM.CARD_TYPE,
  credit_card_expired: REPLACEMENT.PAYMENT.CONFIRM.CREDIT_CARD_EXPIRED,
  credit_card_near_expiration:
    REPLACEMENT.PAYMENT.CONFIRM.CREDIT_CARD_NEAR_EXPIRATION,
  description: REPLACEMENT.PAYMENT.CONFIRM.DESCRIPTION,
  expiration_date: REPLACEMENT.PAYMENT.CONFIRM.EXPIRATION_DATE,
  last_four: REPLACEMENT.PAYMENT.CONFIRM.LAST_FOUR,
  masked_number: REPLACEMENT.PAYMENT.CONFIRM.MASKED_NUMBER,
  payment_reference_id: REPLACEMENT.PAYMENT.CONFIRM.PAYMENT_REFERENCE_ID,
  preferred: REPLACEMENT.PAYMENT.CONFIRM.PREFERRED,
  sequence: REPLACEMENT.PAYMENT.CONFIRM.SEQUENCE,
  payment_type: REPLACEMENT.PAYMENT.CONFIRM.PAYMENT_TYPE
};

/**
 * Sets a field inside paymentConfirmState
 * @private
 * @param {FieldNames} field
 * @param {unknown} value
 * @returns {Object} {type: string, path: [], value: object}
 */
function setField(field, value) {
  const path = FIELD_PATHS[field];

  return {
    type: GMI.SET_GMI_STATE,
    path,
    value
  };
}

/**
 * Clears a field inside paymentConfirmState
 * @private
 * @param {FieldNames} field
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearField(field) {
  const path = FIELD_PATHS[field];

  return {
    type: GMI.SET_GMI_STATE,
    path,
    value: null
  };
}

/**
 * Setter for the ui.replacement.payment.confirm data
 * @private
 * @param {Record<FieldNames, unknown>} fields - Key-value pairs of fields and values to set
 * @returns {Object} Batched action that sets each passed field
 */
const setState = (fields = {}) => {
  const batchedActions = [];

  Object.entries(fields).forEach(([key, value]) => {
    if (FIELD_PATHS[key] && value) {
      batchedActions.push(setField(key, value));
    }
  });

  return batchActions(...batchedActions);
};

/**
 * Unsetter for the ui.replacement.payment.confirm data
 * @private
 * @returns {Object} Unsetter action object
 */
function clearState() {
  const batchedActions = [];

  Object.keys(FIELD_PATHS).forEach((field) => {
    batchedActions.push(clearField(field));
  });

  return batchActions(...batchedActions);
}

export const paymentConfirmState = {
  /**
   * @name set
   * @desc A global setter for ui.replacement.payment.confirm data
   * @param {String} [field]
   * @param {Object} [value]
   * @returns {Function}
   * @example
   *
   * // Set paymentConfirm payment_reference_id
   * store.dispatch(actions.replacement.payment.confirmState.set('payment_reference_id', ''));
   *
   * // Set paymentConfirm state
   * store.dispatch(actions.replacement.payment.confirmState.set(null, {}));
   *
   */
  set(field = null, value = {}) {
    return (dispatch) => {
      if (!types(field).isString) return dispatch(setState(value));
      if (FIELD_PATHS[field]) return dispatch(setField(field, value));
    };
  },

  /**
   * @name unset
   * @desc A global unsetter for ui.replacement.payment.confirm data
   * @param {FieldNames | null} [field]
   * @param {unknown} [args]
   * @returns {Function}
   * @memberof actions.replacement
   * @example
   *
   * // Unset paymentConfirm payment_reference_id
   * store.dispatch(actions.replacement.payment.confirmState.unset('payment_reference_id'));
   *
   * // Unset paymentConfirm state
   * store.dispatch(actions.replacement.payment.confirmState.unset());
   *
   */
  unset(type = null) {
    return (dispatch) => {
      if (!types(type).isString) return dispatch(clearState());
      if (FIELD_PATHS[type]) return dispatch(clearField(type));
    };
  }
};

/**
 * Hydration state
 */

/**
 * Helper function to get the corrected payment method for hydration
 * @param {Immutable.Map<any, any>} session
 * @private
 * @returns {any | null}
 */
function getSelectedPaymentMethod(session) {
  const sessionPaymentMethod = session
    .getIn(["replacement", "payment_response", "payment_detail"])
    ?.toJS();

  const profilePaymentMethod = session
    .getIn([
      "replacement",
      "ticket",
      "driver_info",
      "payment_profile",
      "payment_methods",
      0
    ])
    ?.toJS();

  if (isNonEmptyObject(sessionPaymentMethod)) return sessionPaymentMethod;
  if (isNonEmptyObject(profilePaymentMethod)) return profilePaymentMethod;
  return null;
}

/**
 * @desc gets data from session to be saved to `gmi.ui.reservations.driver.paymentConfirm` state
 * @param {Immutable.Map<any, any>} session - GMA session
 * @returns {Record<FieldNames, unknown>}
 */
export function getPaymentConfirmState(session) {
  const selectedPaymentMethod = getSelectedPaymentMethod(session);

  if (!selectedPaymentMethod) return null;

  return {
    card_holder_name: selectedPaymentMethod.card_holder_name,
    card_type: selectedPaymentMethod.card_type,
    credit_card_expired: selectedPaymentMethod.credit_card_expired,
    credit_card_near_expiration:
      selectedPaymentMethod.credit_card_near_expiration,
    description: selectedPaymentMethod.description,
    expiration_date: selectedPaymentMethod.expiration_date,
    last_four: selectedPaymentMethod.last_four || selectedPaymentMethod.masked_number,
    payment_reference_id: selectedPaymentMethod.payment_reference_id,
    preferred: selectedPaymentMethod.preferred,
    sequence: selectedPaymentMethod.sequence,
    payment_type: selectedPaymentMethod.payment_type
  };
}

/**
 * Services
 */

/**
 * Calls payment/initiate endpoint
 * @param {PaymentInitiatePayload} payload
 * @returns {Function} Dispatched service action
 */
export function paymentInitiate(payload) {
  return (dispatch) => {
    return dispatch(
      request({
        service: replacement.paymentInitiate,
        data: payload
      })
    );
  };
}

/**
 * Calls payment/recordPaymentTransaction endpoint
 * @returns {Function} Dispatched service action
 */
export function paymentRecordPaymentTransaction() {
  return (dispatch, getState) => {
    const state = getState();

    const data = {
      session_id: state.getIn(
        STATE.GMA.REPLACEMENT.PAYMENT.INITIATE.SUBMISSION_CONTEXT_DATA
          .SESSION_ID
      )
    };
    return dispatch(
      request({ service: replacement.paymentRecordPaymentTransaction, data })
    ).then((res) => {
      const paymentDetail = res?.payment_detail;

      dispatch(paymentConfirmState.set(null, paymentDetail));
    });
  };
}

/**
 * Calls payment/confirm endpoint
 * @returns {Function} Dispatched service action
 */
export function paymentConfirm() {
  return (dispatch, getState) => {
    const state = getState();

    const data = {
      payment_reference_id: state.getIn(
        STATE.UI.REPLACEMENT.PAYMENT.CONFIRM.PAYMENT_REFERENCE_ID
      )
    };

    return dispatch(request({ service: replacement.paymentConfirm, data }));
  };
}

export default {
  paymentConfirmState,
  getPaymentConfirmState,
  paymentInitiate,
  paymentRecordPaymentTransaction,
  paymentConfirm
};
